import { useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import useSWR from 'swr';
import { Callout } from '@workspace/silk-ui';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Copy,
  Database,
  ExternalLink,
  FileText,
  Loader2,
  Map as MapIcon,
  Route,
  UploadCloud,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@workspace/ui';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import { useNavigation } from '@/context/NavigationContext';
import {
  clearPersistedMapSourceUploadSnapshot,
  describeMapSourceUploadTerminalError,
  estimateMapSourceUploadProgress,
  isMapSourceUploadBackgroundProcessing,
  isMapSourceUploadTerminalStatus,
  loadPersistedMapSourceUploadSnapshot,
  persistMapSourceUploadSnapshot,
  recentMapSourceUploadProcessingLogs,
  uploadMapSourcePart,
} from '@/lib/mapSourceUploadSession';
import {
  abortMapSourceUpload,
  completeMapSourceUpload,
  getMapSourceUpload,
  initMapSourceUpload,
  listMapSourceUploads,
  listMapSources,
  presignMapSourceUploadPart,
  recalculateMapSourceSalesDistances,
  type MapSource,
  type MapSourceUploadSession,
  type ShareScope,
} from '@/lib/mapping-api';
import {
  getTenantPublicMapSettings,
  updateTenantPublicMapSettings,
  type PublicMapSettings,
  type TenantPublicMapSettingsResponse,
} from '@/lib/public-map';
import {
  SALES_MAP_CANONICAL_LAYER_ORDER,
  isSalesMapLayerReady,
  isSalesMapWorkflowReady,
  pickBestSalesMapSourceCollection,
  summarizeSalesMapLayerIssues,
  summarizeSalesMapPackageIssues,
  summarizeSalesMapWorkflowIssues,
  type SalesMapCanonicalLayerKey,
  type SalesMapSourceLayerSelection,
} from '@/lib/salesMapSourcePackage';
import { useMappingAccess } from '@/lib/useMappingAccess';
import { useTenantFeatureMap } from '@/lib/useTenantFeatureMap';

// ─── Types ───────────────────────────────────────────────────────────────────

type UploadMode = 'geojson_file' | 'pmtiles';

type LayerDraft = {
  mode: UploadMode;
  name: string;
  description: string;
  shareScope: ShareScope;
  file: File | null;
};

type LayerGuide = {
  key: Extract<SalesMapCanonicalLayerKey, 'sales_locations' | 'network_fiber' | 'fiber_access_points'>;
  title: string;
  description: string;
  geometry: 'Point' | 'Line' | 'Polygon';
  required: boolean;
  icon: LucideIcon;
  requiredFields: string[];
};

type ValidationCheckStatus = 'pass' | 'fail' | 'warning';

type ValidationCheck = {
  id: string;
  label: string;
  status: ValidationCheckStatus;
  detail: string;
};

type ValidationResult = {
  checks: ValidationCheck[];
  canUpload: boolean;
  missingRequiredFields: string[];
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SALES_LAYER_GUIDES: LayerGuide[] = [
  {
    key: 'sales_locations',
    title: 'Locations',
    description:
      'Addresses and buildings your sales team can select on the map. This GeoJSON must already be enriched with business / POI classification fields before upload. Required.',
    geometry: 'Point',
    required: true,
    icon: MapIcon,
    requiredFields: [
      'feature_id',
      'feature_identity_key',
      'display_name',
      'location_kind',
      'address_line1',
      'locality',
      'region_code',
      'postal_code',
      'serviceability_status',
      'occupancy_kind',
      'matched_place_count',
      'business_category',
    ],
  },
  {
    key: 'network_fiber',
    title: 'Fiber',
    description:
      'Fiber route linework visible as map context. Optional — helps sellers understand network coverage when making sales decisions.',
    geometry: 'Line',
    required: false,
    icon: Route,
    requiredFields: ['feature_id', 'feature_identity_key', 'fiber_segment_id'],
  },
  {
    key: 'fiber_access_points',
    title: 'Access Points',
    description:
      'Splice cases, cabinets, and pedestals shown as point markers. Required — distance enrichment and click-time routing depend on this layer.',
    geometry: 'Point',
    required: true,
    icon: Database,
    requiredFields: ['feature_id', 'feature_identity_key', 'access_point_id', 'access_point_kind'],
  },
];

const LAYER_GUIDE_MAP = Object.fromEntries(
  SALES_LAYER_GUIDES.map(g => [g.key, g]),
) as Record<LayerGuide['key'], LayerGuide>;

const inputCls =
  'h-11 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

const PUBLIC_MAP_LAYER_KEYS: Array<Extract<SalesMapCanonicalLayerKey, 'sales_locations' | 'network_fiber' | 'fiber_access_points'>> = [
  'network_fiber',
  'fiber_access_points',
  'sales_locations',
];

const PUBLIC_MAP_LAYER_LABELS: Record<(typeof PUBLIC_MAP_LAYER_KEYS)[number], string> = {
  network_fiber: 'Fiber',
  fiber_access_points: 'Access Points',
  sales_locations: 'Locations',
};

const splitHostnameList = (value: string) =>
  value
    .split(/[\n,]+/)
    .map(entry => entry.trim().toLowerCase())
    .filter(Boolean);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const buildDefaultDraft = (guide: LayerGuide, tenantName?: string | null): LayerDraft => ({
  mode: 'geojson_file',
  name: [tenantName?.trim(), guide.title].filter(Boolean).join(' ') || guide.title,
  description: `Sales map ${guide.title.toLowerCase()} dataset`,
  shareScope: 'tenant',
  file: null,
});

const formatBytes = (bytes?: number | null) => {
  if (!Number.isFinite(bytes)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Number(bytes);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
  return `${value >= 10 || i === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[i]}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Unknown';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Unknown' : d.toLocaleString();
};

const countCanonicalLayersInSource = (selection: SalesMapSourceLayerSelection<MapSource> | null) => {
  if (!selection) return 0;
  return SALES_MAP_CANONICAL_LAYER_ORDER.filter(id => selection.summary.layers[id].present).length;
};

const isActiveSalesDistanceSession = (session: MapSourceUploadSession | null | undefined) =>
  Boolean(session && !isMapSourceUploadTerminalStatus(session.status));

const formatSalesDistanceSessionStatus = (session: MapSourceUploadSession | null | undefined) => {
  if (!session) return null;
  return estimateMapSourceUploadProgress(session).phaseLabel;
};

const describeUploadSessionPhase = (session: MapSourceUploadSession) => {
  return estimateMapSourceUploadProgress(session).phaseLabel;
};

async function doUploadSession(
  file: File,
  options: {
    name: string;
    description?: string;
    shareScope: ShareScope;
    sourceId?: string;
    processingProfile?: 'geojson_to_pmtiles';
    layerId?: string;
    sourceContract?: string;
    jobCategory?: string;
    subjectType?: string;
    subjectId?: string;
    onProgress?: (uploaded: number, total: number, phase: string) => void;
  },
): Promise<MapSourceUploadSession> {
  const session = await initMapSourceUpload({
    filename: file.name,
    mime_type:
      file.type ||
      'application/geo+json',
    size_bytes: file.size,
    name: options.name,
    description: options.description || undefined,
    share_scope: options.shareScope,
    processing_profile: options.processingProfile,
    layer_id: options.layerId,
    source_contract: options.sourceContract,
    job_category: options.jobCategory,
    subject_type: options.subjectType,
    subject_id: options.subjectId,
    source_id: options.sourceId,
  });

  if (!session.id) throw new Error('Upload session was not created.');

  const partSize = session.part_size_bytes && session.part_size_bytes > 0 ? session.part_size_bytes : file.size;
  const totalParts =
    session.total_parts && session.total_parts > 0
      ? session.total_parts
      : Math.max(1, Math.ceil(file.size / partSize));
  let uploaded = 0;

  options.onProgress?.(0, file.size, 'Uploading');

  for (let part = 1; part <= totalParts; part++) {
    const start = (part - 1) * partSize;
    const blob = file.slice(start, Math.min(file.size, start + partSize));
    const presigned = await presignMapSourceUploadPart(session.id, part);
    if (!presigned.url) throw new Error(`Presign URL missing for part ${part}.`);
    await uploadMapSourcePart(presigned.url, blob, loaded => {
      options.onProgress?.(uploaded + loaded, file.size, `Uploading part ${part} of ${totalParts}`);
    });
    uploaded += blob.size;
    options.onProgress?.(uploaded, file.size, `Uploaded part ${part} of ${totalParts}`);
  }

  options.onProgress?.(uploaded, file.size, 'Finalizing upload');
  const completed = await completeMapSourceUpload(session.id);
  if (completed.status && completed.status !== 'ready') {
    options.onProgress?.(file.size, file.size, describeUploadSessionPhase(completed));
  }
  return completed;
}

// ─── Client-side GeoJSON validation ──────────────────────────────────────────

const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50 MB

async function validateGeoJSONFile(file: File, guide: LayerGuide): Promise<ValidationResult> {
  const checks: ValidationCheck[] = [];

  // Large-file fast path: heuristic text scan only
  if (file.size > LARGE_FILE_THRESHOLD) {
    const preview = await file.slice(0, 1024 * 1024).text();
    const looksLikeFC =
      preview.includes('"FeatureCollection"') && preview.includes('"features"');
    checks.push({
      id: 'size',
      label: `Large file (${formatBytes(file.size)})`,
      status: 'warning',
      detail: 'Full field validation runs server-side after upload',
    });
    checks.push({
      id: 'type',
      label: looksLikeFC ? 'Appears to be a FeatureCollection' : 'Structure unclear from preview',
      status: looksLikeFC ? 'pass' : 'warning',
      detail: '',
    });
    return { checks, canUpload: true, missingRequiredFields: [] };
  }

  // Full parse
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
    checks.push({ id: 'json', label: 'Valid JSON', status: 'pass', detail: formatBytes(file.size) });
  } catch (e) {
    checks.push({
      id: 'json',
      label: 'Invalid JSON',
      status: 'fail',
      detail: e instanceof SyntaxError ? e.message.split('\n')[0] ?? 'Parse error' : 'Could not parse file',
    });
    return { checks, canUpload: false, missingRequiredFields: [] };
  }

  const root = parsed as Record<string, unknown>;

  if (root.type !== 'FeatureCollection') {
    checks.push({
      id: 'type',
      label: 'Not a FeatureCollection',
      status: 'fail',
      detail: root.type ? `Found type "${String(root.type)}"` : 'Missing "type" field',
    });
    return { checks, canUpload: false, missingRequiredFields: [] };
  }
  checks.push({ id: 'type', label: 'FeatureCollection', status: 'pass', detail: '' });

  const features = root.features;
  if (!Array.isArray(features) || features.length === 0) {
    checks.push({
      id: 'features',
      label: 'No features found',
      status: 'fail',
      detail: !Array.isArray(features) ? 'Missing features array' : 'Empty FeatureCollection',
    });
    return { checks, canUpload: false, missingRequiredFields: [] };
  }
  checks.push({
    id: 'features',
    label: `${features.length.toLocaleString()} feature${features.length === 1 ? '' : 's'}`,
    status: 'pass',
    detail: '',
  });

  // Geometry type check (sample first 50 features)
  const geometryTypes = new Set<string>();
  for (let i = 0; i < Math.min(50, features.length); i++) {
    const geom = (features[i] as Record<string, unknown>)?.geometry as Record<string, unknown> | null;
    if (geom?.type) geometryTypes.add(String(geom.type));
  }
  if (geometryTypes.size > 0) {
    const typeList = [...geometryTypes];
    const expected = guide.geometry;
    const matches =
      (expected === 'Point' && typeList.some(t => t.toLowerCase().includes('point'))) ||
      (expected === 'Line' && typeList.some(t => t.toLowerCase().includes('line') || t.toLowerCase().includes('string'))) ||
      (expected === 'Polygon' && typeList.some(t => t.toLowerCase().includes('polygon')));
    checks.push({
      id: 'geometry',
      label: `Geometry: ${typeList.join(', ')}`,
      status: matches ? 'pass' : 'warning',
      detail: matches ? '' : `Expected ${expected} geometry`,
    });
  }

  // Required field check on first feature
  const firstProps = ((features[0] as Record<string, unknown>)?.properties ?? {}) as Record<string, unknown>;
  const propKeys = new Set(Object.keys(firstProps).map(k => k.toLowerCase()));
  const missingRequiredFields = guide.requiredFields.filter(f => !propKeys.has(f.toLowerCase()));

  if (guide.requiredFields.length > 0) {
    checks.push({
      id: 'fields',
      label:
        missingRequiredFields.length === 0
          ? `All ${guide.requiredFields.length} required columns present`
          : `Missing ${missingRequiredFields.length} required column${missingRequiredFields.length === 1 ? '' : 's'}`,
      status: missingRequiredFields.length === 0 ? 'pass' : 'fail',
      detail: missingRequiredFields.length > 0 ? missingRequiredFields.join(', ') : '',
    });
  }

  return {
    checks,
    canUpload: !checks.some(c => c.status === 'fail'),
    missingRequiredFields,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LayerSidebarItem({
  guide,
  selected,
  ready,
  processing,
  hasError,
  sourceNote,
  onClick,
}: {
  guide: LayerGuide;
  selected: boolean;
  ready: boolean;
  processing: boolean;
  hasError: boolean;
  sourceNote: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
        selected
          ? 'bg-primary/10 text-foreground'
          : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
          selected
            ? 'bg-primary/15 text-primary'
            : ready
              ? 'bg-green-50 text-green-600 dark:bg-green-950'
              : hasError
                ? 'bg-red-50 text-red-500 dark:bg-red-950'
                : 'bg-muted/40 text-muted-foreground group-hover:bg-muted/60',
        )}
      >
        <guide.icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium leading-none">{guide.title}</p>
        {sourceNote ? (
          <p className="mt-1 truncate text-xs opacity-60">{sourceNote}</p>
        ) : null}
      </div>
      <div className="shrink-0">
        {hasError ? (
          <AlertCircle className="h-4 w-4 text-red-500" />
        ) : ready ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : processing ? (
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
        ) : guide.required ? (
          <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-950 dark:text-amber-400">
            Req
          </span>
        ) : null}
      </div>
    </button>
  );
}

function ValidationPanel({
  result,
  isValidating,
}: {
  result: ValidationResult | null;
  isValidating: boolean;
}) {
  if (isValidating) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Checking file…</p>
      </div>
    );
  }
  if (!result) return null;

  const iconFor = (status: ValidationCheckStatus) => {
    if (status === 'pass') return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" />;
    if (status === 'fail') return <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />;
    return <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />;
  };

  return (
    <div className="rounded-lg border border-border/60 bg-background/60 px-4 py-3">
      <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        File validation
      </p>
      <div className="space-y-1.5">
        {result.checks.map(check => (
          <div key={check.id} className="flex items-start gap-2.5">
            {iconFor(check.status)}
            <span
              className={cn(
                'text-sm',
                check.status === 'fail'
                  ? 'font-medium text-red-700 dark:text-red-400'
                  : check.status === 'warning'
                    ? 'text-amber-700 dark:text-amber-400'
                    : 'text-foreground',
              )}
            >
              {check.label}
              {check.detail ? (
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">{check.detail}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequiredColumnsPanel({
  guide,
  missingFields,
}: {
  guide: LayerGuide;
  missingFields: string[] | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const copyAll = () => void navigator.clipboard.writeText(guide.requiredFields.join(', '));
  const missingSet = new Set(missingFields ?? []);

  return (
    <div className="overflow-hidden rounded-lg border border-border/60">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/20"
        onClick={() => setExpanded(v => !v)}
      >
        <span className="text-sm font-medium text-foreground">
          Required columns{' '}
          <span className="font-normal text-muted-foreground">({guide.requiredFields.length})</span>
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={e => {
              e.stopPropagation();
              copyAll();
            }}
          >
            <Copy className="h-3 w-3" />
            Copy all
          </button>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>
      {expanded ? (
        <div className="border-t border-border/60 px-4 py-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Your file must include properties with these exact column names:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {guide.requiredFields.map(field => {
              const missing = missingSet.has(field);
              const verified = missingFields !== null && !missing;
              return (
                <span
                  key={field}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-xs',
                    missing
                      ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'
                      : verified
                        ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400'
                        : 'border-border/60 bg-muted/30 text-foreground',
                  )}
                >
                  {missing ? (
                    <XCircle className="h-2.5 w-2.5" />
                  ) : verified ? (
                    <CheckCircle2 className="h-2.5 w-2.5" />
                  ) : null}
                  {field}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FileDropZone({
  mode,
  file,
  onFile,
  onClear,
  disabled,
}: {
  mode: UploadMode;
  file: File | null;
  onFile: (f: File) => void;
  onClear: () => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const accept = '.geojson,.json,application/geo+json,application/json';

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div
      className={cn(
        'relative rounded-xl border-2 border-dashed transition-all',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : file
            ? 'border-border bg-muted/20'
            : isDragOver
              ? 'cursor-copy border-primary bg-primary/5'
              : 'cursor-pointer border-border/60 hover:border-primary/40 hover:bg-muted/10',
      )}
      onClick={() => { if (!disabled && !file) inputRef.current?.click(); }}
      onDragOver={e => { e.preventDefault(); if (!disabled) setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={accept}
        disabled={disabled}
        onChange={e => {
          const f = e.target.files?.[0] ?? null;
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {file ? (
        <div className="flex items-center gap-4 px-5 py-4">
          <FileText className="h-9 w-9 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={e => { e.stopPropagation(); onClear(); }}
          >
            Remove
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 px-8 py-12 text-center">
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-xl transition-colors',
              isDragOver ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground',
            )}
          >
            <UploadCloud className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {isDragOver ? 'Drop to stage file' : 'Drop your file here, or click to browse'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {mode === 'geojson_file' ? 'Accepts .geojson and .json' : 'Accepts .pmtiles files'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Layer editor ─────────────────────────────────────────────────────────────

interface LayerEditorProps {
  guide: LayerGuide;
  draft: LayerDraft;
  onChange: (updater: (current: LayerDraft) => LayerDraft) => void;
  selection: SalesMapSourceLayerSelection<MapSource> | null;
  packageReady: boolean;
  busy: boolean;
  uploadPhase: string | null;
  uploadedBytes: number;
  totalBytes: number;
  error: string | null;
  justUploaded: boolean;
  backgroundProcessing: boolean;
  backgroundPhase: string | null;
  backgroundSession: MapSourceUploadSession | null;
  recentSessions: MapSourceUploadSession[];
  layerIssues: string[];
  onUpload: () => void;
  onCancelProcessing: () => void;
  onSkip?: () => void;
}

function LayerEditor({
  guide,
  draft,
  onChange,
  selection,
  packageReady,
  busy,
  uploadPhase,
  uploadedBytes,
  totalBytes,
  error,
  justUploaded,
  backgroundProcessing,
  backgroundPhase,
  backgroundSession,
  recentSessions,
  layerIssues,
  onUpload,
  onCancelProcessing,
  onSkip,
}: LayerEditorProps) {
  const [fileValidation, setFileValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const controlsDisabled = busy;

  useEffect(() => {
    if (!draft.file || draft.mode !== 'geojson_file') {
      setFileValidation(null);
      setIsValidating(false);
      return;
    }
    let cancelled = false;
    setIsValidating(true);
    setFileValidation(null);
    void validateGeoJSONFile(draft.file, guide)
      .then(r => { if (!cancelled) { setFileValidation(r); setIsValidating(false); } })
      .catch(() => { if (!cancelled) setIsValidating(false); });
    return () => { cancelled = true; };
  }, [draft.file, draft.mode, guide]);

  const layerSourceCount = countCanonicalLayersInSource(selection);
  const canReplaceSource = Boolean(selection?.source.id) && layerSourceCount <= 1;
  const missingFields = fileValidation?.missingRequiredFields ?? null;
  const hasValidationFailure = fileValidation !== null && !fileValidation.canUpload;
  const canSubmit = Boolean(draft.file) && !busy;
  const backgroundProgress = backgroundSession ? estimateMapSourceUploadProgress(backgroundSession) : null;

  const uploadLabel = (() => {
    if (busy) return 'Uploading…';
    if (backgroundProcessing) return 'Processing…';
    if (justUploaded || (packageReady && !draft.file)) return 'Upload replacement';
    return 'Upload';
  })();

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-8 py-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {guide.required ? (
            <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
              Required
            </span>
          ) : (
            <Badge variant="outline">Optional</Badge>
          )}
          <Badge variant="outline">{guide.geometry}</Badge>
          {packageReady ? <Badge variant="outline">Valid + bound</Badge> : null}
        </div>
        <h2 className="text-xl font-semibold text-foreground">{guide.title}</h2>
        <p className="text-sm text-muted-foreground">{guide.description}</p>
      </div>

      {/* Current active source */}
      {selection?.source.name || selection?.source.id ? (
        <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Currently active source
          </p>
          <p className="mt-1 text-sm font-medium text-foreground">
            {selection.source.name ?? selection.source.id}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {canReplaceSource
              ? 'Uploading a new file will replace this source.'
              : 'Uploading will register a new source alongside this one.'}
          </p>
        </div>
      ) : null}

      {/* Status banners */}
      {error ? (
        <Callout variant="error">
          {error}
          {!error.toLowerCase().includes('column') && !error.toLowerCase().includes('field') ? (
            <> Check the required columns below and verify your file before retrying.</>
          ) : null}
        </Callout>
      ) : null}

      {justUploaded ? (
        <Callout variant="success">
          <strong>{guide.title}</strong> uploaded successfully — the layer is now active and bound to the managed sales
          map contract.
        </Callout>
      ) : packageReady && !justUploaded ? (
        <Callout variant="success">
          This layer is already active, contract-valid, and bound to the managed sales map. Upload a replacement file
          to update it.
        </Callout>
      ) : null}

      {backgroundProcessing ? (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Converting in the background</p>
            {backgroundPhase ? (
              <p className="mt-0.5 text-xs text-blue-600 dark:text-blue-400">{backgroundPhase}</p>
            ) : null}
            <p className="mt-0.5 text-xs text-blue-600 dark:text-blue-400">
              You can upload a replacement to cancel this attempt, or stop it now and try again.
            </p>
          </div>
          {backgroundSession?.id ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 border-blue-300 bg-white/70 text-blue-700 hover:bg-white"
              onClick={onCancelProcessing}
              disabled={busy}
            >
              Cancel processing
            </Button>
          ) : null}
        </div>
      ) : null}

      {backgroundProcessing && backgroundProgress ? (
        <div className="rounded-lg border border-border/60 bg-background px-4 py-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-foreground">{backgroundProgress.phaseLabel}</p>
            <p className="tabular-nums text-xs text-muted-foreground">
              {backgroundProgress.percent === null ? 'Working…' : `${backgroundProgress.percent}%`}
            </p>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full bg-primary transition-all duration-150 ${
                backgroundProgress.indeterminate ? 'animate-pulse' : ''
              }`}
              style={{
                width: `${
                  backgroundProgress.percent === null
                    ? 35
                    : Math.max(4, Math.min(100, backgroundProgress.percent))
                }%`,
              }}
            />
          </div>
          {backgroundProgress.elapsedLabel ? (
            <p className="mt-2 text-xs text-muted-foreground">Elapsed {backgroundProgress.elapsedLabel}</p>
          ) : null}
        </div>
      ) : null}

      {layerIssues.length > 0 && !backgroundProcessing && !error ? (
        <Callout variant="warning">
          <div className="space-y-2">
            <p className="font-medium">This layer still has contract issues.</p>
            <ul className="list-disc pl-5">
              {layerIssues.map(issue => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        </Callout>
      ) : null}

      {/* Upload section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Upload a file</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Format:</span>
            <select
              className="h-8 rounded-md border border-border/60 bg-background px-3 text-xs font-medium text-foreground outline-none transition-all focus:border-primary/40"
              value={draft.mode}
              disabled={controlsDisabled}
              onChange={e =>
                onChange(current => ({
                  ...current,
                  mode: e.target.value as UploadMode,
                  file: null,
                }))
              }
            >
              <option value="geojson_file">GeoJSON (recommended)</option>
            </select>
          </div>
        </div>

        <FileDropZone
          mode={draft.mode}
          file={draft.file}
          onFile={f => onChange(current => ({ ...current, file: f }))}
          onClear={() => onChange(current => ({ ...current, file: null }))}
          disabled={controlsDisabled}
        />

        <ValidationPanel result={fileValidation} isValidating={isValidating} />

        {hasValidationFailure ? (
          <Callout variant="warning">
            Your file has issues that may prevent it from working on the Sales Map. Fix the problems above or upload anyway to let the server attempt processing.
          </Callout>
        ) : null}

        {busy && totalBytes > 0 ? (
          <div className="rounded-lg border border-border/60 bg-background px-4 py-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground">{uploadPhase ?? 'Uploading'}</p>
              <p className="tabular-nums text-xs text-muted-foreground">
                {formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}
              </p>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all duration-150"
                style={{ width: `${Math.max(4, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)))}%` }}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Required columns reference */}
      <RequiredColumnsPanel guide={guide} missingFields={missingFields} />

      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-2">
        <div>
          {!guide.required && onSkip && !busy && !backgroundProcessing ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onSkip}
            >
              Skip for now
            </Button>
          ) : null}
        </div>
        <Button type="button" disabled={!canSubmit} onClick={onUpload}>
          {backgroundProcessing ? 'Cancel and upload replacement' : uploadLabel}
        </Button>
      </div>

      {/* Advanced options */}
      <div className="border-t border-border/40 pt-4">
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setShowAdvanced(v => !v)}
        >
          {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Advanced options
        </button>
        {showAdvanced ? (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Source name</Label>
              <Input
                className={inputCls}
                value={draft.name}
                disabled={controlsDisabled}
                onChange={e => onChange(current => ({ ...current, name: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Label for this upload in the source library.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Visibility</Label>
              <Select
                value={draft.shareScope}
                disabled={controlsDisabled}
                onValueChange={v => onChange(current => ({ ...current, shareScope: v as ShareScope }))}
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">This account only</SelectItem>
                  <SelectItem value="shared">Shared across accounts</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Use "This account only" unless instructed otherwise.</p>
            </div>
            <div className="space-y-1.5 lg:col-span-2">
              <Label>Description</Label>
              <Input
                className={inputCls}
                value={draft.description}
                disabled={controlsDisabled}
                onChange={e => onChange(current => ({ ...current, description: e.target.value }))}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Recent activity */}
      {recentSessions.length > 0 ? (
        <div className="border-t border-border/40 pt-4">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Recent activity
          </p>
          <div className="space-y-2">
            {recentSessions.map(session => (
              <div
                key={session.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-border/60 bg-muted/10 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {session.filename ?? session.name ?? 'Upload session'}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(session.updated_at)}</p>
                  {session.error_message ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{session.error_message}</p>
                  ) : null}
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">
                  {describeUploadSessionPhase(session)}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SalesDataEditorPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const { featureMap } = useTenantFeatureMap();
  const mappingAccess = useMappingAccess();
  const salesAccessEnabled = !!featureMap?.['sales.runtime.enabled'];
  const tenantId = activeTenant?.id;
  const tenantName = activeTenant?.name ?? null;

  const [selectedLayerId, setSelectedLayerId] = useState<LayerGuide['key']>(SALES_LAYER_GUIDES[0].key);
  const [layerDrafts, setLayerDrafts] = useState<Record<LayerGuide['key'], LayerDraft>>(() =>
    Object.fromEntries(SALES_LAYER_GUIDES.map(g => [g.key, buildDefaultDraft(g)])) as Record<LayerGuide['key'], LayerDraft>,
  );
  const [busyLayerId, setBusyLayerId] = useState<LayerGuide['key'] | null>(null);
  const [uploadPhase, setUploadPhase] = useState<string | null>(null);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [layerErrors, setLayerErrors] = useState<Record<LayerGuide['key'], string | null>>(
    () => Object.fromEntries(SALES_LAYER_GUIDES.map(g => [g.key, null])) as Record<LayerGuide['key'], string | null>,
  );
  const [justUploadedLayers, setJustUploadedLayers] = useState<Record<LayerGuide['key'], boolean>>(
    () => Object.fromEntries(SALES_LAYER_GUIDES.map(g => [g.key, false])) as Record<LayerGuide['key'], boolean>,
  );
  const [activeUploadSession, setActiveUploadSession] = useState<MapSourceUploadSession | null>(null);
  const [activeUploadLayerId, setActiveUploadLayerId] = useState<LayerGuide['key'] | null>(null);
  const [distanceJobBusy, setDistanceJobBusy] = useState(false);
  const [distanceJobError, setDistanceJobError] = useState<string | null>(null);
  const [distanceJobNotice, setDistanceJobNotice] = useState<string | null>(null);
  const [publicMapDraft, setPublicMapDraft] = useState<PublicMapSettings | null>(null);
  const [publicMapCustomHostnameText, setPublicMapCustomHostnameText] = useState('');
  const [publicMapSaveBusy, setPublicMapSaveBusy] = useState(false);
  const [publicMapSaveError, setPublicMapSaveError] = useState<string | null>(null);
  const [publicMapSaveNotice, setPublicMapSaveNotice] = useState<string | null>(null);

  // Sync default draft names when tenant loads
  useEffect(() => {
    if (!tenantName) return;
    setLayerDrafts(current =>
      Object.fromEntries(
        SALES_LAYER_GUIDES.map(g => {
          const existing = current[g.key];
          return [g.key, existing.name.trim() ? existing : { ...existing, name: buildDefaultDraft(g, tenantName).name }];
        }),
      ) as Record<LayerGuide['key'], LayerDraft>,
    );
  }, [tenantName]);

  const { data: publicMapConfig, mutate: mutatePublicMapConfig } = useSWR<TenantPublicMapSettingsResponse>(
    tenantId && salesAccessEnabled ? ['tenant-public-map-settings', tenantId] : null,
    () => getTenantPublicMapSettings(tenantId!),
  );

  useEffect(() => {
    if (!publicMapConfig) {
      return;
    }
    setPublicMapDraft(publicMapConfig.settings);
    setPublicMapCustomHostnameText((publicMapConfig.settings.customHostnames ?? []).join('\n'));
  }, [publicMapConfig]);

  const { data: mapSources, mutate } = useSWR<MapSource[]>(
    tenantId && salesAccessEnabled && mappingAccess.accessEnabled ? ['sales-editor-sources', tenantId] : null,
    () => listMapSources({ limit: 5000, share_scope: 'tenant' }),
  );

  const { data: uploadSessions, mutate: mutateUploadSessions } = useSWR<MapSourceUploadSession[]>(
    tenantId && salesAccessEnabled && mappingAccess.accessEnabled ? ['sales-editor-uploads', tenantId] : null,
    () => listMapSourceUploads({ limit: 50, job_category: 'sales_map_layer_upload', subject_type: 'sales_map_layer' }),
    {
      refreshInterval: sessions =>
        (Array.isArray(sessions) ? sessions : []).some(s => isMapSourceUploadBackgroundProcessing(s.status))
          ? 2000
          : 10000,
    },
  );
  const salesPackageCollection = useMemo(() => pickBestSalesMapSourceCollection(mapSources ?? []), [mapSources]);
  const salesPackageSummary = salesPackageCollection?.summary ?? null;
  const salesPackageIssues = useMemo(() => summarizeSalesMapPackageIssues(salesPackageSummary), [salesPackageSummary]);
  const salesWorkflowIssues = useMemo(() => summarizeSalesMapWorkflowIssues(salesPackageSummary), [salesPackageSummary]);
  const salesLocationsSelection = salesPackageCollection?.layers.sales_locations ?? null;
  const accessPointsSelection = salesPackageCollection?.layers.fiber_access_points ?? null;
  const relevantSalesDistanceSession = useMemo(() => {
    const locationsSourceID = salesLocationsSelection?.source.id ?? '';
    if (!locationsSourceID) return null;
    return (
      [...(uploadSessions ?? [])]
        .filter(session => session.target_source_id === locationsSourceID && session.layer_id === 'sales_locations')
        .sort((a, b) => Date.parse(b.updated_at ?? '') - Date.parse(a.updated_at ?? ''))[0] ?? null
    );
  }, [salesLocationsSelection?.source.id, uploadSessions]);
  const canRunSalesDistanceJob = Boolean(salesLocationsSelection?.source.id);
  const salesDistanceJobActive = isActiveSalesDistanceSession(relevantSalesDistanceSession);
  const salesDistanceProcessingLogs = useMemo(
    () => recentMapSourceUploadProcessingLogs(relevantSalesDistanceSession, 8),
    [relevantSalesDistanceSession],
  );

  useEffect(() => {
    setPublicMapDraft(current => {
      if (!current) {
        return current;
      }
      const layers = { ...(current.layers ?? {}) };
      for (const layerKey of PUBLIC_MAP_LAYER_KEYS) {
        const selection = salesPackageCollection?.layers[layerKey] ?? null;
        const existing = layers[layerKey] ?? { enabled: layerKey !== 'sales_locations', label: PUBLIC_MAP_LAYER_LABELS[layerKey] };
        layers[layerKey] = {
          ...existing,
          label: existing.label || PUBLIC_MAP_LAYER_LABELS[layerKey],
          sourceId: selection?.source.id ?? existing.sourceId,
          sourceLayerId: selection?.layer.id ?? existing.sourceLayerId,
          geometry: selection?.layer.geometry ?? existing.geometry,
        };
      }
      return { ...current, layers };
    });
  }, [salesPackageCollection]);

  const activeProcessingSessionsByLayer = useMemo(() => {
    const map = new globalThis.Map<LayerGuide['key'], MapSourceUploadSession>();
    for (const s of uploadSessions ?? []) {
      const lid = (s.layer_id ?? null) as LayerGuide['key'] | null;
      if (!lid || !LAYER_GUIDE_MAP[lid] || !isMapSourceUploadBackgroundProcessing(s.status)) continue;
      const current = map.get(lid);
      const cTs = current?.updated_at ? Date.parse(current.updated_at) : 0;
      const sTs = s.updated_at ? Date.parse(s.updated_at) : 0;
      if (!current || sTs >= cTs) map.set(lid, s);
    }
    return map;
  }, [uploadSessions]);

  const recentSessionsByLayer = useMemo(() => {
    const map = new globalThis.Map<LayerGuide['key'], MapSourceUploadSession[]>();
    for (const g of SALES_LAYER_GUIDES) map.set(g.key, []);
    for (const s of uploadSessions ?? []) {
      const lid = (s.layer_id ?? null) as LayerGuide['key'] | null;
      if (!lid || !LAYER_GUIDE_MAP[lid]) continue;
      map.set(lid, [...(map.get(lid) ?? []), s]);
    }
    for (const g of SALES_LAYER_GUIDES) {
      map.set(
        g.key,
        (map.get(g.key) ?? [])
          .sort((a, b) => Date.parse(b.updated_at ?? '') - Date.parse(a.updated_at ?? ''))
          .slice(0, 3),
      );
    }
    return map;
  }, [uploadSessions]);

  // Restore persisted upload session on mount
  useEffect(() => {
    const persisted = loadPersistedMapSourceUploadSnapshot();
    if (!persisted?.sessionId || !persisted.layerId || !LAYER_GUIDE_MAP[persisted.layerId as LayerGuide['key']]) return;
    if ((uploadSessions ?? []).some(s => s.id === persisted.sessionId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const restored = await getMapSourceUpload(persisted.sessionId);
        if (cancelled) return;
        if (restored.status === 'ready') {
          clearPersistedMapSourceUploadSnapshot();
          await mutate(); await mutateUploadSessions(); return;
        }
        if (isMapSourceUploadTerminalStatus(restored.status)) {
          clearPersistedMapSourceUploadSnapshot(); return;
        }
        setActiveUploadSession(restored);
        setActiveUploadLayerId(persisted.layerId as LayerGuide['key']);
        setUploadPhase(describeUploadSessionPhase(restored));
        setUploadedBytes(restored.size_bytes ?? 0);
        setTotalBytes(restored.size_bytes ?? 0);
      } catch { clearPersistedMapSourceUploadSnapshot(); }
    })();
    return () => { cancelled = true; };
  }, [mutate, mutateUploadSessions, uploadSessions]);

  // Poll active background upload session
  useEffect(() => {
    if (!activeUploadSession?.id || !activeUploadLayerId) return;
    if (!isMapSourceUploadBackgroundProcessing(activeUploadSession.status)) return;
    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      try {
        const latest = await getMapSourceUpload(activeUploadSession.id!);
        if (cancelled) return;
        if (isMapSourceUploadBackgroundProcessing(latest.status)) {
          setActiveUploadSession(latest);
          setUploadPhase(describeUploadSessionPhase(latest));
          await mutateUploadSessions();
          return;
        }
        const lid = activeUploadLayerId;
        if (latest.status === 'ready') {
          clearPersistedMapSourceUploadSnapshot();
          setActiveUploadSession(null); setActiveUploadLayerId(null);
          setUploadPhase(null); setUploadedBytes(0); setTotalBytes(0);
          await mutate(); await mutateUploadSessions();
          setJustUploadedLayers(c => ({ ...c, [lid]: true }));
          return;
        }
        if (isMapSourceUploadTerminalStatus(latest.status)) {
          clearPersistedMapSourceUploadSnapshot();
          setActiveUploadSession(null); setActiveUploadLayerId(null);
          setUploadPhase(null); setUploadedBytes(0); setTotalBytes(0);
          await mutateUploadSessions();
          const title = LAYER_GUIDE_MAP[lid].title;
          const msg = describeMapSourceUploadTerminalError(title, latest);
          setLayerErrors(c => ({ ...c, [lid]: msg }));
        }
      } catch { /* retry next interval */ }
    }, 2000);
    return () => { cancelled = true; window.clearInterval(intervalId); };
  }, [activeUploadLayerId, activeUploadSession, mutate, mutateUploadSessions]);

  // Refresh sources while any background processing is active
  useEffect(() => {
    const hasActive = [...activeProcessingSessionsByLayer.values()].some(s =>
      isMapSourceUploadBackgroundProcessing(s.status),
    );
    if (!hasActive) return;
    const id = window.setInterval(() => { void mutate(); }, 4000);
    return () => window.clearInterval(id);
  }, [activeProcessingSessionsByLayer, mutate]);

  const setDraftForLayer = (layerId: LayerGuide['key'], updater: (c: LayerDraft) => LayerDraft) =>
    setLayerDrafts(c => ({ ...c, [layerId]: updater(c[layerId]) }));

  const handleUpload = async (guide: LayerGuide) => {
    const draft = layerDrafts[guide.key];
    const existingProcessingSession = activeProcessingSessionsByLayer.get(guide.key) ?? null;
    setLayerErrors(c => ({ ...c, [guide.key]: null }));
    setJustUploadedLayers(c => ({ ...c, [guide.key]: false }));

    if (!draft.file) {
      setLayerErrors(c => ({ ...c, [guide.key]: 'Select a GeoJSON file to upload.' }));
      return;
    }
    if (!draft.name.trim()) {
      setLayerErrors(c => ({ ...c, [guide.key]: 'Source name is required. Open Advanced options to set one.' }));
      return;
    }

    const currentSelection = salesPackageCollection?.layers[guide.key] ?? null;
    const sourceCount = countCanonicalLayersInSource(currentSelection);
    const replaceSourceId =
      sourceCount <= 1
        ? currentSelection?.source.id ?? undefined
        : undefined;

    setBusyLayerId(guide.key);
    setUploadPhase('Preparing upload');
    setUploadedBytes(0);
    setTotalBytes(draft.file.size);
    setActiveUploadSession(null);
    setActiveUploadLayerId(null);
    clearPersistedMapSourceUploadSnapshot();
    let keepProgress = false;

    try {
      if (existingProcessingSession?.id) {
        setUploadPhase('Cancelling current conversion');
        await abortMapSourceUpload(existingProcessingSession.id);
        await mutateUploadSessions();
      }

      const completed = await doUploadSession(draft.file, {
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        shareScope: draft.shareScope,
        sourceId: replaceSourceId,
        processingProfile: 'geojson_to_pmtiles',
        layerId: guide.key,
        sourceContract: guide.key,
        jobCategory: 'sales_map_layer_upload',
        subjectType: 'sales_map_layer',
        subjectId: guide.key,
        onProgress: (up, tot, phase) => {
          setUploadPhase(phase); setUploadedBytes(up); setTotalBytes(tot);
        },
      });

      setLayerDrafts(c => ({ ...c, [guide.key]: { ...c[guide.key], file: null } }));

      if (completed.status === 'ready') {
        await mutate(); await mutateUploadSessions();
        setJustUploadedLayers(c => ({ ...c, [guide.key]: true }));
      } else {
        persistMapSourceUploadSnapshot({
          sessionId: completed.id!,
          fileName: draft.file.name,
          fileSize: draft.file.size,
          fileLastModified: draft.file.lastModified,
          name: draft.name,
          description: draft.description,
          shareScope: draft.shareScope,
          sourceKind: draft.mode,
          processingProfile: 'geojson_to_pmtiles',
          layerId: guide.key,
        });
        setActiveUploadSession(completed);
        setActiveUploadLayerId(guide.key);
        setUploadPhase(describeUploadSessionPhase(completed));
        setUploadedBytes(completed.size_bytes ?? draft.file.size);
        setTotalBytes(completed.size_bytes ?? draft.file.size);
        keepProgress = true;
        void mutateUploadSessions();
      }
    } catch (e) {
      setLayerErrors(c => ({
        ...c,
        [guide.key]: e instanceof Error ? e.message : 'Upload failed. Check your file format and required column names.',
      }));
    } finally {
      setBusyLayerId(null);
      if (!keepProgress) { setUploadPhase(null); setUploadedBytes(0); setTotalBytes(0); }
    }
  };

  const handleCancelProcessing = async (guide: LayerGuide) => {
    const session = activeProcessingSessionsByLayer.get(guide.key) ?? null;
    if (!session?.id) return;
    setLayerErrors(c => ({ ...c, [guide.key]: null }));
    setBusyLayerId(guide.key);
    try {
      await abortMapSourceUpload(session.id);
      if (activeUploadLayerId === guide.key) {
        setActiveUploadSession(null);
        setActiveUploadLayerId(null);
        setUploadPhase(null);
        setUploadedBytes(0);
        setTotalBytes(0);
        clearPersistedMapSourceUploadSnapshot();
      }
      await mutateUploadSessions();
      await mutate();
    } catch (e) {
      setLayerErrors(c => ({
        ...c,
        [guide.key]: e instanceof Error ? e.message : 'Failed to cancel background processing.',
      }));
    } finally {
      setBusyLayerId(null);
    }
  };

  const handleStartSalesDistanceJob = async () => {
    const locationsSourceID = salesLocationsSelection?.source.id;
    if (!locationsSourceID) {
      setDistanceJobError('Locations must be uploaded before distance enrichment can start.');
      return;
    }
    setDistanceJobBusy(true);
    setDistanceJobError(null);
    setDistanceJobNotice(null);
    try {
      const session = await recalculateMapSourceSalesDistances(locationsSourceID);
      setActiveUploadSession(session);
      setActiveUploadLayerId('sales_locations');
      setUploadPhase(describeUploadSessionPhase(session));
      setUploadedBytes(session.size_bytes ?? 0);
      setTotalBytes(session.size_bytes ?? 0);
      persistMapSourceUploadSnapshot({
        sessionId: session.id,
        fileName: session.filename ?? 'sales_locations.geojson',
        fileSize: session.size_bytes ?? 0,
        fileLastModified: 0,
        replaceTargetId: session.target_source_id ?? locationsSourceID,
        name: session.name ?? 'Sales Locations',
        description: null,
        shareScope: (session.share_scope as ShareScope | undefined) ?? 'tenant',
        sourceKind: 'geojson_file',
        processingProfile: 'geojson_to_pmtiles',
        layerId: 'sales_locations',
      });
      await mutateUploadSessions();
      if (session.status === 'waiting_for_access_points') {
        setDistanceJobNotice('Distance enrichment is queued and will resume automatically after Access Points are uploaded.');
      } else {
        setDistanceJobNotice('Distance enrichment started from the existing uploaded GeoJSON.');
      }
    } catch (error) {
      setDistanceJobError(error instanceof Error ? error.message : 'Failed to start distance enrichment.');
    } finally {
      setDistanceJobBusy(false);
    }
  };

  const handleSavePublicMapSettings = async () => {
    if (!tenantId || !publicMapDraft) {
      return;
    }
    setPublicMapSaveBusy(true);
    setPublicMapSaveError(null);
    setPublicMapSaveNotice(null);
    try {
      const nextSettings: PublicMapSettings = {
        ...publicMapDraft,
        customHostnames: splitHostnameList(publicMapCustomHostnameText),
        layers: Object.fromEntries(
          PUBLIC_MAP_LAYER_KEYS.map(layerKey => {
            const selection = salesPackageCollection?.layers[layerKey] ?? null;
            const existing = publicMapDraft.layers?.[layerKey] ?? { enabled: layerKey !== 'sales_locations' };
            return [
              layerKey,
              {
                ...existing,
                label: existing.label || PUBLIC_MAP_LAYER_LABELS[layerKey],
                sourceId: selection?.source.id ?? existing.sourceId,
                sourceLayerId: selection?.layer.id ?? existing.sourceLayerId,
                geometry: selection?.layer.geometry ?? existing.geometry,
              },
            ];
          }),
        ),
      };
      const saved = await updateTenantPublicMapSettings(tenantId, nextSettings);
      setPublicMapDraft(saved.settings);
      setPublicMapCustomHostnameText((saved.settings.customHostnames ?? []).join('\n'));
      await mutatePublicMapConfig(saved, false);
      setPublicMapSaveNotice('Public website settings saved.');
    } catch (error) {
      setPublicMapSaveError(error instanceof Error ? error.message : 'Failed to save public website settings.');
    } finally {
      setPublicMapSaveBusy(false);
    }
  };

  // ─── Access gates ───────────────────────────────────────────────────────────

  if (!salesAccessEnabled) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold text-foreground">Commerce Data</h1>
        <Callout variant="warning">The active tenant does not have the sales-linked data workspace enabled.</Callout>
      </div>
    );
  }
  if (!mappingAccess.mappingFeatureEnabled) {
    return (
      <MappingFeatureUnavailable
        intro="Mapping is disabled for the current tenant, so the sales dataset editor cannot register map sources."
        title="Mapping runtime unavailable"
        description="Enable tenant mapping before setting up sales datasets."
      />
    );
  }
  if (!mappingAccess.mappingPermissionGranted) return <MappingPermissionDenied />;

  const selectedGuide = LAYER_GUIDE_MAP[selectedLayerId];
  const currentIndex = SALES_LAYER_GUIDES.findIndex(g => g.key === selectedLayerId);
  const nextGuide = SALES_LAYER_GUIDES[currentIndex + 1] ?? null;

  const isLayerBusy = (key: LayerGuide['key']) =>
    busyLayerId === key ||
    activeProcessingSessionsByLayer.has(key) ||
    (activeUploadLayerId === key && isMapSourceUploadBackgroundProcessing(activeUploadSession?.status));

  const isLayerReady = (key: LayerGuide['key']) => {
    const summary = salesPackageSummary?.layers[key];
    return Boolean(summary && isSalesMapLayerReady(summary));
  };
  const currentLayerIssues = summarizeSalesMapLayerIssues(salesPackageSummary?.layers[selectedLayerId]);
  const salesDistanceActionLabel = (() => {
    if (distanceJobBusy) return 'Starting…';
    if (salesDistanceJobActive) {
      if (relevantSalesDistanceSession?.status === 'waiting_for_access_points') return 'Waiting for access points';
      const progress = relevantSalesDistanceSession ? estimateMapSourceUploadProgress(relevantSalesDistanceSession) : null;
      if (typeof progress?.percent === 'number') {
        return `Calculating… ${Math.round(progress.percent)}%`;
      }
      return 'Calculating…';
    }
    if (!accessPointsSelection?.source.id) return 'Queue distance enrichment';
    return 'Recalculate distances';
  })();
  const publicMapCanManage = publicMapConfig?.canManage ?? false;
  const publicMapDnsTarget = publicMapConfig?.dnsTarget ?? '';
  const publicMapDefaultHostname = publicMapDraft?.defaultHostname ?? '';
  const publicMapPrimaryHostname = publicMapDefaultHostname || publicMapDraft?.hostnames?.[0] || '';
  const publicMapSharedSiteURL = publicMapConfig?.sharedSiteUrl ?? '';
  const publicMapSharedEmbedURL = publicMapConfig?.sharedEmbedUrl ?? '';
  const publicMapEmbedURL = publicMapSharedEmbedURL || (publicMapPrimaryHostname ? `https://${publicMapPrimaryHostname}/?embed=1` : '');
  const publicMapEmbedCode = publicMapEmbedURL
    ? `<iframe src="${publicMapEmbedURL}" title="${tenantName ?? 'Tenant'} fiber map" width="100%" height="640" style="border:0;" loading="lazy" allowfullscreen></iframe>`
    : '';

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border/60 bg-background px-5 py-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-muted-foreground"
            onClick={() => navigation.navigate('/commerce')}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Commerce
          </Button>
          <div className="h-4 w-px bg-border" />
          <h1 className="text-sm font-semibold text-foreground">Commerce Data</h1>
        </div>
        <div className="flex items-center gap-2.5">
          {salesPackageSummary ? (
            <Badge variant="outline">{salesPackageSummary.label}</Badge>
          ) : null}
          <Badge variant="outline">
            {salesPackageSummary && isSalesMapWorkflowReady(salesPackageSummary)
              ? 'Sales workflow ready'
              : 'Business classification required'}
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigation.navigate('/sales')}
          >
            Open Sales Map
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="flex w-56 shrink-0 flex-col border-r border-border/60 bg-muted/10">
          <div className="flex-1 overflow-y-auto px-2 py-3">
            <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Required
            </p>
            {SALES_LAYER_GUIDES.filter(g => g.required).map(g => (
              <LayerSidebarItem
                key={g.key}
                guide={g}
                selected={selectedLayerId === g.key}
                ready={isLayerReady(g.key)}
                processing={isLayerBusy(g.key)}
                hasError={Boolean(layerErrors[g.key])}
                sourceNote={salesPackageCollection?.layers[g.key]?.source.name ?? null}
                onClick={() => setSelectedLayerId(g.key)}
              />
            ))}
            <p className="mb-1 mt-4 px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Optional layers
            </p>
            {SALES_LAYER_GUIDES.filter(g => !g.required).map(g => (
              <LayerSidebarItem
                key={g.key}
                guide={g}
                selected={selectedLayerId === g.key}
                ready={isLayerReady(g.key)}
                processing={isLayerBusy(g.key)}
                hasError={Boolean(layerErrors[g.key])}
                sourceNote={salesPackageCollection?.layers[g.key]?.source.name ?? null}
                onClick={() => setSelectedLayerId(g.key)}
              />
            ))}
          </div>
          <div className="shrink-0 border-t border-border/60 px-2 py-2">
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              onClick={() => navigation.navigate('/maps/sources')}
            >
              <Database className="h-3.5 w-3.5" />
              View source library
            </button>
          </div>
        </div>

        {/* Editor panel */}
        <div className="flex-1 overflow-y-auto">
          <div className="border-b border-border/60 bg-muted/10 px-6 py-4">
            {salesPackageSummary ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{salesPackageSummary.description}</p>
                {salesPackageIssues.length > 0 ? (
                  <Callout variant="warning">
                    <div className="space-y-2">
                      <p className="font-medium">Sales package needs fixes before the map is fully ready.</p>
                      <ul className="list-disc pl-5">
                        {salesPackageIssues.map(issue => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  </Callout>
                ) : salesWorkflowIssues.length > 0 ? (
                  <Callout variant="warning">
                    <div className="space-y-2">
                      <p className="font-medium">Business classification data is still missing.</p>
                      <ul className="list-disc pl-5">
                        {salesWorkflowIssues.map(issue => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  </Callout>
                ) : (
                  <Callout variant="success">All required package checks are satisfied.</Callout>
                )}
              </div>
            ) : (
              <Callout variant="warning">
                Upload the required Locations dataset to activate the Sales Map.
              </Callout>
            )}
          </div>
          <div className="border-b border-border/60 bg-background px-6 py-5">
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Public website</p>
                    <p className="text-xs text-muted-foreground">
                      Publish selected sales-map layers to the tenant&apos;s public hostname list.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <input
                      type="checkbox"
                      checked={Boolean(publicMapDraft?.enabled)}
                      disabled={!publicMapCanManage || !publicMapDraft}
                      onChange={event =>
                        setPublicMapDraft(current => (current ? { ...current, enabled: event.target.checked } : current))
                      }
                    />
                    Enable public site
                  </label>
                </div>
                {!publicMapCanManage ? (
                  <Callout variant="warning">
                    Only tenant owners can change hostname bindings or public website publication settings for this tenant.
                  </Callout>
                ) : null}
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Default hostname</Label>
                    <div className="rounded-lg border border-border/60 bg-muted/10 px-4 py-3">
                      <p className="font-mono text-sm text-foreground">
                        {publicMapDefaultHostname || 'Default hostname unavailable.'}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This platform-managed hostname is assigned automatically and is the default embed and TLS-ready address for the public map.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Optional custom hostnames</Label>
                    <textarea
                      className="min-h-32 w-full rounded-lg border border-border/60 bg-background px-4 py-3 text-sm text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                      placeholder="fiber.kevinsfiberco.com&#10;map.kevinsfiberco.com"
                      value={publicMapCustomHostnameText}
                      disabled={!publicMapCanManage}
                      onChange={event => setPublicMapCustomHostnameText(event.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Add one custom domain per line. Only tenant owners can change these optional hostnames.
                    </p>
                  </div>
                </div>
                <div className="space-y-3 rounded-xl border border-border/60 bg-muted/10 p-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">DNS target</p>
                    <p className="font-mono text-sm text-foreground">
                      {publicMapDnsTarget || 'Set PUBLIC_MAP_DNS_TARGET to show the environment target.'}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tenant-owned domains should point to the deployment target above. The default platform hostname is already managed by the platform.
                  </p>
                  <div className="space-y-1 border-t border-border/50 pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PoC URL</p>
                    <p className="font-mono text-sm text-foreground">
                      {publicMapSharedSiteURL || 'Set PUBLIC_MAP_SHARED_BASE_URL to generate the shared-host demo URL.'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      This shared-host URL is the fastest proof-of-concept path while per-tenant DNS and automated custom-domain activation are still being finished.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-xl border border-border/60 bg-muted/10 p-4">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">Embed</p>
                  <p className="text-xs text-muted-foreground">
                    Use embed mode to place the public map inside the tenant&apos;s own website with the map filling the iframe bounds.
                  </p>
                </div>
                {publicMapEmbedCode ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Generated from the {publicMapSharedEmbedURL ? 'shared-host proof-of-concept URL' : 'default platform hostname'}:{' '}
                      <span className="font-mono text-foreground">{publicMapSharedEmbedURL || publicMapPrimaryHostname}</span>
                    </p>
                    <textarea
                      readOnly
                      className="min-h-28 w-full rounded-lg border border-border/60 bg-background px-4 py-3 font-mono text-xs text-foreground outline-none"
                      value={publicMapEmbedCode}
                    />
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    The embed snippet will appear once a default hostname is available.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Published layers</Label>
                <div className="space-y-2">
                  {PUBLIC_MAP_LAYER_KEYS.map(layerKey => {
                    const selection = salesPackageCollection?.layers[layerKey] ?? null;
                    const layerDraft = publicMapDraft?.layers?.[layerKey];
                    const isAvailable = Boolean(selection?.source.id && selection?.layer.id);
                    return (
                      <label
                        key={layerKey}
                        className="flex items-start justify-between gap-4 rounded-lg border border-border/60 bg-background px-4 py-3"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">{PUBLIC_MAP_LAYER_LABELS[layerKey]}</p>
                          <p className="text-xs text-muted-foreground">
                            {selection?.source.name ?? 'No canonical source is currently available for this layer.'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={Boolean(layerDraft?.enabled)}
                          disabled={!publicMapCanManage || !publicMapDraft || !isAvailable}
                          onChange={event =>
                            setPublicMapDraft(current =>
                              current
                                ? {
                                    ...current,
                                    layers: {
                                      ...(current.layers ?? {}),
                                      [layerKey]: {
                                        ...(current.layers?.[layerKey] ?? {}),
                                        enabled: event.target.checked,
                                        label: current.layers?.[layerKey]?.label || PUBLIC_MAP_LAYER_LABELS[layerKey],
                                      },
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {publicMapSaveError ? <p className="text-sm text-destructive">{publicMapSaveError}</p> : null}
              {publicMapSaveNotice ? <p className="text-sm text-emerald-700">{publicMapSaveNotice}</p> : null}

              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  onClick={() => void handleSavePublicMapSettings()}
                  disabled={!publicMapCanManage || !publicMapDraft || publicMapSaveBusy}
                >
                  {publicMapSaveBusy ? 'Saving…' : 'Save public website'}
                </Button>
              </div>
            </div>
          </div>
          {selectedGuide ? (
            <>
              {selectedGuide.key === 'sales_locations' && salesLocationsSelection?.source.id ? (
                <div className="border-b border-border/60 bg-background px-6 py-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">Distance enrichment</p>
                      <p className="text-xs text-muted-foreground">
                        Reuse the existing uploaded GeoJSON to recalculate nearest fiber access point distances without reuploading the Locations file.
                      </p>
                      {relevantSalesDistanceSession ? (
                        <p className="text-xs text-muted-foreground">
                          Latest processing: {formatSalesDistanceSessionStatus(relevantSalesDistanceSession)}
                          {typeof estimateMapSourceUploadProgress(relevantSalesDistanceSession).percent === 'number'
                            ? ` (${Math.round(estimateMapSourceUploadProgress(relevantSalesDistanceSession).percent ?? 0)}%)`
                            : ''}
                        </p>
                      ) : null}
                      {salesDistanceProcessingLogs.length ? (
                        <div className="rounded-md bg-muted/20 p-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Processing log
                          </p>
                          <div className="mt-1 space-y-1">
                            {salesDistanceProcessingLogs.map(line => (
                              <p key={line} className="break-words font-mono text-[11px] text-muted-foreground">
                                {line}
                              </p>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {distanceJobNotice ? <p className="text-xs text-emerald-700">{distanceJobNotice}</p> : null}
                      {distanceJobError ? <p className="text-xs text-destructive">{distanceJobError}</p> : null}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleStartSalesDistanceJob()}
                      disabled={distanceJobBusy || salesDistanceJobActive || !canRunSalesDistanceJob}
                    >
                      {salesDistanceActionLabel}
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          {selectedGuide ? (
            <LayerEditor
              key={selectedLayerId}
              guide={selectedGuide}
              draft={layerDrafts[selectedLayerId]}
              onChange={updater => setDraftForLayer(selectedLayerId, updater)}
              selection={salesPackageCollection?.layers[selectedLayerId] ?? null}
              packageReady={isLayerReady(selectedLayerId)}
              busy={busyLayerId === selectedLayerId}
              uploadPhase={
                busyLayerId === selectedLayerId ||
                (activeUploadLayerId === selectedLayerId && isMapSourceUploadBackgroundProcessing(activeUploadSession?.status))
                  ? uploadPhase
                  : null
              }
              uploadedBytes={busyLayerId === selectedLayerId ? uploadedBytes : 0}
              totalBytes={busyLayerId === selectedLayerId ? totalBytes : 0}
              error={layerErrors[selectedLayerId]}
              justUploaded={justUploadedLayers[selectedLayerId]}
              backgroundProcessing={isLayerBusy(selectedLayerId) && busyLayerId !== selectedLayerId}
              backgroundSession={activeProcessingSessionsByLayer.get(selectedLayerId) ?? null}
              backgroundPhase={
                activeUploadLayerId === selectedLayerId
                  ? uploadPhase
                  : activeProcessingSessionsByLayer.has(selectedLayerId)
                    ? describeUploadSessionPhase(activeProcessingSessionsByLayer.get(selectedLayerId)!)
                    : null
              }
              recentSessions={recentSessionsByLayer.get(selectedLayerId) ?? []}
              layerIssues={currentLayerIssues}
              onUpload={() => void handleUpload(selectedGuide)}
              onCancelProcessing={() => void handleCancelProcessing(selectedGuide)}
              onSkip={
                selectedGuide.required || !nextGuide
                  ? undefined
                  : () => setSelectedLayerId(nextGuide.key)
              }
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
